import { inject, Injectable } from '@angular/core'
import { eps } from './url.service'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Business } from '../store/business/business.state'
import { catchError, EMPTY, map } from 'rxjs'
import { ToastrService } from 'ngx-toastr'
import { Router } from '@angular/router'

export interface Licence {
  id: string
  subscriptionId: string
  totalLicences: number
  usedLicences: number
  chargeAmount: number
  amount: number
  nextBillingDate: string
  createdAt: string
  updatedAt: string
  deletedAt: string
}

export interface AddLicencesSummary {
  currentLicences: number
  newTotalLicences: number
  addedLicences: number
  proratedCost: number
  availableCredit: number
  creditApplied: number
  amountDueToday: number
  currency: string
}

export interface AddLicencesNextBilling {
  newRecurringAmount: number
  nextBillingDate: string // Often comes as an ISO string from JSON
}

export interface PreviewAddLicencesResponse {
  summary: AddLicencesSummary
  nextBilling: AddLicencesNextBilling
  paymentNeeded: boolean
}

export enum SubscriptionStatus {
  Active = 'active',
  Cancelled = 'cancelled',
  Expired = 'expired',
  PastDue = 'past_due',
  Trialing = 'trialing',
  Upgraded = 'upgraded'
}

export interface subscription {
  subscriptionDisplayId: string
  belongsTo: string
  subscriptionDate: string
  planName: string
  planType: string
  planId: string
  minimumLicences: number
  planCurrency: string
  planCurrencyName: string
  planCurrencySymbol: string
  planPrice: number
  discountType: string
  discountValue: number
  priceAfterDiscount: number
  availableFeatures: string[]
  trialDays: number
  expiryDate: string | null
  licencesForNextCycle: number | null
  description: string
  subscriptionStatus: string
  parentSubscriptionId: string | null
  licences?: Licence[]
}

export interface incomingSubscription extends subscription {
  id: string
  business?: Business
  createdAt: string
  updatedAt: string
  deletedAt: string
}

export interface SubscriptionListResponse {
  data: incomingSubscription[]
  total: number
}

export interface SubscriptionAnalytics {
  totalSubscriptions: number
  activeSubscriptions: number
  inactiveSubscriptions: number
  trialSubscriptions: number
}

export interface FeatureItem {
  text: string
  isFeature: boolean
  icon: string | null
  colorClass: string | null
}

export interface PlanView {
  id: string
  name: string
  description: string
  type: 'MONTHLY' | 'YEARLY'
  position: number
  minimumLicences: number
  price: string
  discountType: 'FIXED' | 'PERCENTAGE'
  discountValue: number
  availableFeatures?: string[]
  trialDays: number
  isRecommended: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
  deletedAt?: string | null
  totalSubscribers?: number
  hasDiscount: boolean
  originalPrice: string | null
  featuresList: FeatureItem[]
  hiddenFeaturesList: FeatureItem[]
  hasMoreFeatures: boolean
  billingCycleText: string
  planCurrency?: {
    currency_symbol?: string
  }
}

export interface PlansForBusiness {
  monthlyPlans: PlanView[]
  yearlyPlans: PlanView[]
}

export interface PlanDetails {
  name: string
  type: string // 'monthly' or 'yearly'
  pricePerLicence: number
}

export interface CurrentPlanInfo extends PlanDetails {
  currentLicences: number
  recurringAmount: number
  planStartDate: Date
  planEndDate: Date
}

export interface NewPlanInfo extends PlanDetails {
  requestedLicences: number
  minimumLicences: number
}

interface CalculationBreakdown {
  cycleTotalDays: number
  cycleRemainingDays: number
  pricePerDay: number
  proratedAmount: number
}

export interface BillingSummary {
  oldPlanCalculation: CalculationBreakdown
  newPlanCalculation: CalculationBreakdown
  proratedCreditFromOldPlan: number
  proratedCostForNewPlan: number
  immediateChargeBeforeCredit: number
  existingCreditBalanceApplied: number
  amountDueToday: number
  creditGeneratedOnDowngrade: number
  nextBillingDate: Date
  nextRecurringAmount: number
  currency: string
  isUpgrade: boolean
  isDowngrade: boolean
  isFrequencyChange: boolean
  usedAmount: number
  unusedCredit: number
}

export interface PreviewPlanChangeResponse {
  currentPlan: CurrentPlanInfo
  newPlan: NewPlanInfo
  billingSummary: BillingSummary
}

interface PlanActivationInfo {
  name: string
  price: number
  discount: number
  discountType: 'FIXED' | 'PERCENTAGE'
  discountedPrice: number
  billingFrequency: 'MONTHLY' | 'YEARLY'
  requestedLicences: number
  minimumLicences: number
  usedLicences: number
  remainingLicences: number
  recurringAmount: number
  nextBillingDate: Date
}

interface PlanActivationBillingSummary {
  priceAfterDiscount: number
  cycleStartDate: Date
  cycleEndDate: Date
  remainingDaysInCycle: number
  pricePerDay: number
  amountToBePaid: number
  currency: string
}

export interface PreviewPlanActivationResponse {
  plan: PlanActivationInfo
  billingSummary: PlanActivationBillingSummary
}

@Injectable({
  providedIn: 'root'
})
export class SubscriptionsService {
  api = eps.planAndBilling
  http = inject(HttpClient)
  toastr = inject(ToastrService)
  router = inject(Router)

  getsubscriptionHistory(subscriptionDisplayId: string) {
    let params = new HttpParams()
    params = params.set('subsId', subscriptionDisplayId)
    return this.http.get<incomingSubscription>(this.api.subscriptionHistoryApi, { params })
  }

  getSubscriptions(belongsTo: string) {
    let params = new HttpParams()
    params = params.set('belongsTo', belongsTo)
    return this.http.get<incomingSubscription[]>(this.api.planApi, { params })
  }

  getPlansForBusiness(belongsTo: string) {
    let params = new HttpParams()
    params = params.set('belongsTo', belongsTo)
    return this.http.get<PlansForBusiness>(this.api.getPlansForBusinessApi, { params })
  }

  list(filters: {
    type?: string | null
    status?: string | null
    search?: string | null
    page?: number
    limit?: number
  }) {
    let params = new HttpParams()
    if (filters.page) params = params.set('page', filters.page.toString())
    if (filters.limit) params = params.set('limit', filters.limit.toString())
    if (filters.type) params = params.set('type', filters.type)
    if (filters.status) params = params.set('status', filters.status)
    if (filters.search) params = params.set('search', filters.search)
    return this.http.get<SubscriptionListResponse>(this.api.allSubscribersApi, { params })
  }

  getAnalytics() {
    return this.http.get<SubscriptionAnalytics>(this.api.subscribersAnalyticsApi)
  }

  previewAddLicences(subscriptionId: string, totalLicences: number) {
    return this.http.post<PreviewAddLicencesResponse>(this.api.previewAddLicencesApi, {
      subscriptionId,
      newTotalLicences: totalLicences
    })
  }

  updateTrialLicenseLimit(subscriptionId: string, totalLicences: number) {
    return this.http.put<incomingSubscription>(this.api.addLicencesApi, {
      subscriptionId,
      newTotalLicences: totalLicences
    })
  }

  removeLicences(subscriptionId: string, totalLicences: number) {
    return this.http.post<incomingSubscription>(this.api.removeLicencesApi, {
      subscriptionId,
      newTotalLicences: totalLicences
    })
  }

  updateSubscriptionStatus(subscriptionId: string, subscriptionStatus: SubscriptionStatus) {
    return this.http.put<incomingSubscription>(this.api.updateSubscriptionStatusApi, {
      subscriptionId,
      subscriptionStatus
    })
  }

  updateTrialDays(subscriptionId: string, trialDays: number) {
    return this.http.put<incomingSubscription>(this.api.updateTrialDaysApi, {
      subscriptionId,
      trialDays
    })
  }

  previewPlanChange(
    currentSubscriptionId: string,
    newPlanId: string,
    businessId: string,
    licencesCount?: number
  ) {
    let params = new HttpParams()
    params = params.set('currentSubscriptionId', currentSubscriptionId)
    params = params.set('newPlanId', newPlanId)
    params = params.set('businessId', businessId)
    if (licencesCount) params = params.set('licencesCount', licencesCount)
    return this.http
      .get<PreviewPlanChangeResponse>(this.api.changePlanSummaryApi, {
        params
      })
      .pipe(
        map(response => response),
        catchError(error => {
          this.toastr.error(error.error.message)
          this.router.navigate([this.api.planPath])
          return EMPTY
        })
      )
  }

  previewPlanActivation(
    currentSubscriptionId: string,
    newPlanId: string,
    businessId: string,
    licencesCount: number | null
  ) {
    let params = new HttpParams()
    params = params.set('currentSubscriptionId', currentSubscriptionId)
    params = params.set('newPlanId', newPlanId)
    params = params.set('businessId', businessId)
    if (licencesCount) params = params.set('licencesCount', licencesCount)
    return this.http
      .get<PreviewPlanActivationResponse>(this.api.planActivationSummaryApi, {
        params
      })
      .pipe(
        map(response => response),
        catchError(error => {
          this.toastr.error(error.error.message)
          //this.router.navigate([this.api.planPath])
          return EMPTY
        })
      )
  }
}
